<template>
  <div v-if="show" class="modal-backdrop">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Crop Image</h5>
          <button type="button" class="btn-close" @click="$emit('close')">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="img-container">
            <img
              ref="image"
              :src="imageSrc"
              alt="To Crop"
              :crossorigin="isCrossOrigin ? 'anonymous' : undefined"
            />
          </div>
        </div>
        <div class="modal-footer justify-content-between">
          <div class="btn-group">
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="rotate(-90)"
              title="Rotate Left"
            >
              <i class="fas fa-undo"></i>
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="rotate(90)"
              title="Rotate Right"
            >
              <i class="fas fa-redo"></i>
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="zoom(0.1)"
              title="Zoom In"
            >
              <i class="fas fa-search-plus"></i>
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="zoom(-0.1)"
              title="Zoom Out"
            >
              <i class="fas fa-search-minus"></i>
            </button>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="reset"
              title="Reset"
            >
              <i class="fas fa-sync"></i>
            </button>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-secondary me-2"
              @click="$emit('close')"
            >
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="crop">
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";

export default {
  name: "ImageCropperModal",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    image: {
      type: [String, File],
      required: true,
    },
    aspectRatio: {
      type: Number,
      default: NaN, // Free
    },
  },
  emits: ["close", "save"],
  data() {
    return {
      cropper: null,
      imageSrc: "",
    };
  },
  computed: {
    isCrossOrigin() {
      if (!this.imageSrc) return false;
      if (
        this.imageSrc.startsWith("data:") ||
        this.imageSrc.startsWith("blob:")
      )
        return false;
      try {
        const imgUrl = new URL(this.imageSrc, window.location.href);
        return imgUrl.origin !== window.location.origin;
      } catch (e) {
        return false;
      }
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.initImage();
      } else {
        this.destroyCropper();
      }
    },
  },
  methods: {
    initImage() {
      if (this.image instanceof File) {
        this.imageSrc = URL.createObjectURL(this.image);
      } else {
        this.imageSrc = this.image;
      }
      this.$nextTick(() => {
        this.initCropper();
      });
    },
    initCropper() {
      if (this.cropper) {
        this.cropper.destroy();
      }
      const image = this.$refs.image;
      this.cropper = new Cropper(image, {
        aspectRatio: this.aspectRatio,
        viewMode: 1,
        autoCropArea: 0.8,
        responsive: true,
      });
    },
    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      // Revoke object URL if created
      if (this.imageSrc.startsWith("blob:")) {
        URL.revokeObjectURL(this.imageSrc);
        this.imageSrc = "";
      }
    },
    rotate(deg) {
      this.cropper?.rotate(deg);
    },
    zoom(ratio) {
      this.cropper?.zoom(ratio);
    },
    reset() {
      this.cropper?.reset();
    },
    crop() {
      if (!this.cropper) return;

      this.cropper
        .getCroppedCanvas({
          maxWidth: 4096,
          maxHeight: 4096,
          fillColor: "#fff",
        })
        .toBlob(
          (blob) => {
            // Create a new File from the blob
            const fileName = `cropped-${Date.now()}.jpg`;
            const file = new File([blob], fileName, { type: "image/jpeg" });
            this.$emit("save", file);
            this.$emit("close");
          },
          "image/jpeg",
          0.9
        );
    },
  },
  beforeUnmount() {
    this.destroyCropper();
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
}

.modal-dialog {
  background: white;
  border-radius: 0.5rem;
  width: 95%;
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 0;
  flex: 1;
  overflow: hidden;
  position: relative;
  background: #2c2c2c;
  min-height: 500px;
}

.img-container {
  width: 100%;
  height: 65vh;
}

.img-container img {
  display: block;
  max-width: 100%;
}

.modal-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  align-items: center;
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #000;
  opacity: 0.5;
  cursor: pointer;
}
.btn-close:hover {
  opacity: 0.75;
}
</style>
